import { useState, useEffect, useMemo } from 'react'
import {
  Container,
  Title,
  Stack,
  Grid,
  Card,
  Text,
  Group,
  Button,
  Loader,
  Center,
  Badge,
  Table,
  ScrollArea,
  TextInput,
  MultiSelect,
  Select,
  Pagination,
  Paper,
  Chip,
  Popover,
  Alert,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { IconCalendar, IconRefresh, IconSearch, IconInfoCircle, IconDownload, IconLoader, IconArrowUp, IconArrowDown, IconMinus, IconChartBar } from '@tabler/icons-react'
import { useRoutingMetrics, useRoutingRows, useOffensesBySearchId, useEvaluationsBySearch, useVersionComparison, useUpdateQaDecision } from '@/hooks/useAnalytics'
import { notifications } from '@mantine/notifications'
import type { EvaluationVersion } from '@/types'
import { exportAllRoutingDashboardRows, type ModelVersionInfo } from '@/services/analytics.api'
import { usePermissions } from '@/hooks/usePermissions'
import { useVersionContext } from '@/context/VersionContext'
import { OffenseDetailDialog } from '@/components/OffenseDetailDialog'
import { SendToExpertButton } from '@/components/SendToExpertButton'
import { BarChart } from '@mantine/charts'

// Columns restricted from expert users (internal analytics columns)
const EXPERT_RESTRICTED_COLUMNS = [
  'type_class',
  'subtype_class',
  'charge_norm',
  'disp_class',
  'disposition_norm',
  'offense_age',
  'age_category',
  'age_at_offense',
  'offense_status',
  'adjudication_category',
  'crime_category',
  'court_type',
  'effective_release_date',
  'sentence_is_serving',
]

// Column definitions for Routing Matrix - matching Reportability Dashboard
const allAvailableColumns = [
  // Core identifiers
  { value: 'search_id', label: 'Search ID' },
  { value: 'offense_id', label: 'Offense ID' },
  { value: 'case_number', label: 'Case Number' },
  { value: 'search_type', label: 'Product' },
  // Charge info
  { value: 'offense_type', label: 'Offense Type' },
  { value: 'type_class', label: 'Type Class' },
  { value: 'subtype_class', label: 'Subtype Class' },
  { value: 'charge', label: 'Charge' },
  { value: 'charge_description', label: 'Charge Desc' },
  { value: 'charge_norm', label: 'Charge Norm' },
  // Disposition info
  { value: 'disposition', label: 'Disposition' },
  { value: 'disp_class', label: 'Disp Class' },
  { value: 'disposition_norm', label: 'Disp Norm' },
  // Dates
  { value: 'label_created_at', label: 'Created Date' },
  { value: 'charge_date', label: 'Charge Date' },
  { value: 'arrest_date', label: 'Arrest Date' },
  { value: 'disposition_date', label: 'Disp Date' },
  // Age/status
  { value: 'offense_age', label: 'Offense Age' },
  { value: 'age_category', label: 'Age Category' },
  { value: 'age_at_offense', label: 'Age at Offense' },
  { value: 'offense_status', label: 'Offense Status' },
  // Categories
  { value: 'adjudication_category', label: 'Adjudication' },
  { value: 'crime_category', label: 'Crime Category' },
  { value: 'court_type', label: 'Court Type' },
  { value: 'source_norm', label: 'Source Norm' },
  // Additional info
  { value: 'additional_info', label: 'Additional Info' },
  { value: 'sentence_info', label: 'Sentence Info' },
  { value: 'effective_release_date', label: 'Release Date' },
  { value: 'sentence_is_serving', label: 'Currently Serving' },
  // Routing-specific columns
  { value: 'identity_level', label: 'AI Identity Match Level' },
  { value: 'reportability', label: 'Reportability' },
  { value: 'queue', label: 'Queue' },
  // Decisions
  { value: 'ce_decision', label: 'AI Decision' },
  { value: 'rules_applied', label: 'Rules Applied' },
  { value: 'rationale', label: 'AI Rationale' },
  { value: 'ground_truth', label: 'UBS Decision' },
  { value: 'qa_decision', label: 'QA Decision' },
  { value: 'all_notes', label: 'Notes' },
  // Status columns
  { value: 'match_status', label: 'Match Status' },
  { value: 'error_type', label: 'Error Type' },
  { value: 'id_mismatch_by_notes', label: 'ID Mismatch (Notes)' },
  { value: 'duplicate_by_notes', label: 'Duplicate (Notes)' },
  { value: 'routing_accuracy', label: 'Routing Accuracy' },
  // Validation
  { value: 'validation_issues', label: 'Validation Issues' },
  { value: 'validation_error_count', label: 'Validation Errors' },
  { value: 'validation_warning_count', label: 'Validation Warnings' },
  // History
  { value: 'current_version_number', label: 'Run #' },
  { value: 'prev_decision', label: 'Prev Decision' },
  { value: 'version_count', label: 'Runs' },
  // Actions
  { value: 'actions', label: 'Actions' },
]

const defaultColumns = [
  'search_id',
  'offense_id',
  'case_number',
  'search_type',
  'source_norm',
  'offense_type',
  'type_class',
  'subtype_class',
  'charge',
  'charge_norm',
  'disposition',
  'disposition_norm',
  'disp_class',
  'disposition_date',
  'offense_age',
  'adjudication_category',
  'identity_level',
  'ce_decision',
  'rules_applied',
  'rationale',
  'ground_truth',
  'qa_decision',
  'all_notes',
]

// Expert users see same default columns minus restricted fields
const expertDefaultColumns = defaultColumns.filter(
  col => !EXPERT_RESTRICTED_COLUMNS.includes(col)
)

/** List is sorted newest-first; previous deploy is the next entry after current. */
function resolveCurrentAndPrevious(versions: ModelVersionInfo[]) {
  if (versions.length === 0) {
    return { current: null as ModelVersionInfo | null, previous: null as ModelVersionInfo | null }
  }
  const currentIdx = versions.findIndex(v => v.isCurrent)
  const current = currentIdx >= 0 ? versions[currentIdx] : versions[0]
  const idx = currentIdx >= 0 ? currentIdx : 0
  const previous = versions[idx + 1] ?? (versions.length > 1 ? versions[1] : null)
  return { current, previous }
}

export function RoutingDashboard() {
  const { isAdmin, isExpert } = usePermissions()

  // Filter available columns based on user role (experts see fewer columns)
  const availableColumns = useMemo(() => {
    if (isExpert) {
      return allAvailableColumns.filter(col => !EXPERT_RESTRICTED_COLUMNS.includes(col.value))
    }
    return allAvailableColumns
  }, [isExpert])

  // Dialog state for offense details
  const [selectedOffense, setSelectedOffense] = useState<any>(null)
  const [dialogOpened, setDialogOpened] = useState(false)
  const [openHistoryTab, setOpenHistoryTab] = useState(false)

  // Version popover state
  const [versionPopoverSearchId, setVersionPopoverSearchId] = useState<string | null>(null)
  const evaluationsForPopover = useEvaluationsBySearch(versionPopoverSearchId)

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    new Date(),
  ])
  const [searchId, setSearchId] = useState('')
  const [queueFilter, setQueueFilter] = useState<string[]>([])
  const [identityLevelFilter, setIdentityLevelFilter] = useState<string[]>([])
  const [reportabilityFilter, setReportabilityFilter] = useState<string[]>([])
  const [matchStatusFilter, setMatchStatusFilter] = useState<string[]>([])
  const [idMatchValidatedFilter, setIdMatchValidatedFilter] = useState<string[]>([])
  const [duplicateFilter, setDuplicateFilter] = useState<string[]>([])
  const [ignoredByUbsFilter, setIgnoredByUbsFilter] = useState<string[]>(['REMAINING'])
  const [searchTypeFilter, setSearchTypeFilter] = useState<string | null>(null)
  const [sourceNormFilter, setSourceNormFilter] = useState<string | null>(null)
  const [ubsExclusionFilter, setUbsExclusionFilter] = useState<string[]>([])
  const [aiExclusionFilter, setAiExclusionFilter] = useState<string[]>([])
  const [typeClassFilter, setTypeClassFilter] = useState<string[]>([])
  const [offenseAgeFilter, setOffenseAgeFilter] = useState<string[]>([])
  const [hasDispDateFilter, setHasDispDateFilter] = useState<string[]>([])
  const [validationStatusFilter, setValidationStatusFilter] = useState<string[]>([])
  const [validationCountFilter, setValidationCountFilter] = useState<string | null>(null)
  const [versionFilter, setVersionFilter] = useState<string | null>(null)
  const [qaOverrideFilter, setQaOverrideFilter] = useState<string[]>([])
  const [tableProductFilter, setTableProductFilter] = useState<string[]>([])
  const { selectedVersion: globalVersion, versions: contextVersions } = useVersionContext()
  const [pageVersionOverride, setPageVersionOverride] = useState<string | null>(null)
  const modelVersionFilter = pageVersionOverride ?? globalVersion

  // Clear page-level override when global version changes so all pages stay in sync
  useEffect(() => {
    setPageVersionOverride(null)
  }, [globalVersion])

  const qaDecisionMutation = useUpdateQaDecision()

  // Fetch all offenses for the selected search_id from database (filtered by active version)
  const { data: offensesBySearchData } = useOffensesBySearchId(selectedOffense?.search_id || null, modelVersionFilter)
  // Use different default columns based on user role
  const initialColumns = isExpert ? expertDefaultColumns : defaultColumns
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialColumns)
  const [page, setPage] = useState(1)

  // Reset columns when user role changes
  useEffect(() => {
    setSelectedColumns(isExpert ? expertDefaultColumns : defaultColumns)
  }, [isExpert])
  const [isExporting, setIsExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState<string | null>(null)
  const [compareEnabled, setCompareEnabled] = useState(false)
  const [compareMode, setCompareMode] = useState<'version' | 'chronological'>('version')
  const [compareV1, setCompareV1] = useState<string | null>(null)
  const [compareV2, setCompareV2] = useState<string | null>(null)
  const pageSize = 50

  const modelVersions = contextVersions

  const formatModelVersionLabel = (version: string, isCurrent?: boolean) => {
    const name = version === 'pre-versioning' ? 'Pre-versioning' : version
    return isCurrent ? `${name} (current)` : name
  }

  useEffect(() => {
    if (modelVersions.length === 0) return

    const { current, previous } = resolveCurrentAndPrevious(modelVersions)
    if (current && compareV2 !== current.version) setCompareV2(current.version)
    if (previous && !compareV1) setCompareV1(previous.version)
  }, [modelVersions]) // eslint-disable-line react-hooks/exhaustive-deps

  const filterParams = {
    startDate: dateRange[0]?.toISOString().split('T')[0] || '',
    endDate: dateRange[1]?.toISOString().split('T')[0] || '',
    searchId,
    queue: queueFilter,
    identityLevel: identityLevelFilter,
    reportability: reportabilityFilter,
    matchStatus: matchStatusFilter,
    idMatchValidated: idMatchValidatedFilter,
    duplicate: duplicateFilter,
    ubsStatus: ignoredByUbsFilter,
    searchType: searchTypeFilter && searchTypeFilter !== '__ALL__' ? [searchTypeFilter] : [],
    sourceNorm: sourceNormFilter && sourceNormFilter !== '__ALL__' ? [sourceNormFilter] : [],
    ubsExclusion: ubsExclusionFilter,
    aiExclusion: aiExclusionFilter,
    typeClass: typeClassFilter,
    offenseAge: offenseAgeFilter,
    hasDispDate: hasDispDateFilter,
    validationStatus: validationStatusFilter,
    validationCount: validationCountFilter || '',
    versionFilter: versionFilter || '',
    modelVersion: modelVersionFilter || '',
    qaOverride: qaOverrideFilter,
  }

  const selectedModelVersionInfo = modelVersions.find(v => v.version === modelVersionFilter)

  const { data: metricsData, isLoading: isMetricsLoading, refetch: refetchMetrics } = useRoutingMetrics(filterParams)
  const rowsSearchType = tableProductFilter.length > 0
    ? tableProductFilter
    : filterParams.searchType
  const { data: rowsData, isLoading: isRowsLoading, isFetching: isRowsFetching, refetch: refetchRows } = useRoutingRows({
    ...filterParams,
    searchType: rowsSearchType,
    page,
    pageSize,
  })

  const compareUsesVersions = compareMode === 'version' && !!compareV1 && !!compareV2
  const { data: comparisonData, isLoading: isComparisonLoading, isFetching: isComparisonFetching, isError: isComparisonError, error: comparisonError } = useVersionComparison(
    {
      startDate: filterParams.startDate,
      endDate: filterParams.endDate,
      searchType: filterParams.searchType,
      sourceNorm: filterParams.sourceNorm,
      v1: compareV1 || undefined,
      v2: compareV2 || undefined,
      mode: compareUsesVersions ? 'version' : 'chronological',
    },
    compareEnabled,
  )

  // Full-page spinner when metrics are loading (date change) or no row data at all (initial load)
  const isLoading = (isMetricsLoading && !metricsData) || (!rowsData && isRowsLoading)
  const refetch = () => { refetchMetrics(); refetchRows() }

  useEffect(() => {
    setPage(1)
  }, [dateRange, searchId, queueFilter, identityLevelFilter, reportabilityFilter, matchStatusFilter, idMatchValidatedFilter, duplicateFilter, ignoredByUbsFilter, searchTypeFilter, sourceNormFilter, ubsExclusionFilter, aiExclusionFilter, typeClassFilter, offenseAgeFilter, hasDispDateFilter, validationStatusFilter, validationCountFilter, versionFilter, modelVersionFilter, qaOverrideFilter, tableProductFilter])

  const rows = rowsData?.rows || []
  const totalCount = rowsData?.totalCount || 0
  const stats = metricsData?.metrics || null
  const fmt = (v: number | null | undefined) => {
    const n = Number(v ?? 0)
    return (Number.isFinite(n) ? n : 0).toLocaleString()
  }
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  // Download ALL filtered data as CSV (paginates until every matching row is fetched)
  const downloadCSV = async () => {
    if (totalCount === 0) return

    setIsExporting(true)
    setExportProgress(`0 / ${totalCount.toLocaleString()}`)
    try {
      const exportResponse = await exportAllRoutingDashboardRows(
        filterParams,
        (fetched, total) => {
          setExportProgress(`${fetched.toLocaleString()} / ${total.toLocaleString()}`)
        },
      )

      const allRows = exportResponse.rows || []
      if (allRows.length === 0) {
        setIsExporting(false)
        return
      }

      const allowedColumnValues = new Set(availableColumns.map(c => c.value))
      const columnsToExport = selectedColumns.filter(
        col => col !== 'actions' && allowedColumnValues.has(col),
      )
      if (columnsToExport.length === 0) {
        alert('Select at least one column to export.')
        setIsExporting(false)
        return
      }

      // Create CSV header with column labels
      const header = columnsToExport.map(col => {
        const columnDef = allAvailableColumns.find(c => c.value === col)
        return columnDef?.label || col.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
      }).join(',')

      // Helper to render value for CSV
      const renderValueForCSV = (value: any) => {
        if (value === null || value === undefined) return ''
        if (typeof value === 'boolean') return value ? 'Yes' : 'No'
        if (Array.isArray(value)) return value.join('; ')
        if (typeof value === 'object') {
          // BigQuery DATE/TIMESTAMP fields come as { value: "YYYY-MM-DD" }
          if (value?.value != null) return String(value.value)
          return JSON.stringify(value)
        }
        return String(value)
      }

      // Create CSV rows using all fetched data and selected columns
      const csvRows = allRows.map((record: any) => {
        return columnsToExport.map(col => {
          const value = record[col]
          const stringValue = renderValueForCSV(value)
          // Escape quotes and wrap in quotes if contains comma or quotes
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`
          }
          return stringValue
        }).join(',')
      })

      const csv = [header, ...csvRows].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `routing_export_${new Date().toISOString().slice(0, 10)}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
      alert('Export failed. Please try again.')
    } finally {
      setIsExporting(false)
      setExportProgress(null)
    }
  }

  const comparison = comparisonData
  const isVersionComparison = comparison?.mode === 'version'
  const chronologicalComparison = comparison?.mode === 'chronological' ? comparison : null
  const versionComparison = comparison?.mode === 'version' ? comparison : null

  const versionCompareStats = (() => {
    if (!versionComparison || !compareV1 || !compareV2) return null
    const left = versionComparison.v1.version === compareV1 ? versionComparison.v1.metrics : versionComparison.v2.metrics
    const right = versionComparison.v2.version === compareV2 ? versionComparison.v2.metrics : versionComparison.v1.metrics
    if (!left || !right) return null
    return { v1Stats: left, v2Stats: right }
  })()

  const compareVersionsWithNoReplayData = useMemo(() => {
    if (!compareEnabled || compareMode !== 'version') return [] as string[]
    const missing = new Set<string>()
    for (const version of [compareV1, compareV2]) {
      if (!version) continue
      const info = modelVersions.find(v => v.version === version)
      if (info && info.runCount === 0) missing.add(version)
    }
    if (versionCompareStats && compareV1 && versionCompareStats.v1Stats.totalOffenses === 0) {
      missing.add(compareV1)
    }
    if (versionCompareStats && compareV2 && versionCompareStats.v2Stats.totalOffenses === 0) {
      missing.add(compareV2)
    }
    return [...missing]
  }, [compareEnabled, compareMode, compareV1, compareV2, modelVersions, versionCompareStats])

  const summaryStats = stats

  const heatmapData = useMemo(() => {
    if (!summaryStats?.heatmap) return []
    return summaryStats.heatmap.map(entry => ({
      idLevel: entry.identity_level || 'Unknown',
      reportability: entry.reportability || 'Unknown',
      total: entry.total,
      autoCount: entry.autoCount,
      auditorCount: entry.auditorCount,
      insuffCount: entry.insuffCount,
      exclusionCount: entry.exclusionCount || 0,
      exclusionAutoCount: entry.exclusionAutoCount || 0,
      exclusionAuditorCount: entry.exclusionAuditorCount || 0,
      exclusionInsuffCount: entry.exclusionInsuffCount || 0,
      aiExclusionCount: entry.aiExclusionCount || 0,
      aiExclusionAutoCount: entry.aiExclusionAutoCount || 0,
      bothExclusionCount: entry.bothExclusionCount || 0,
      bothExclusionAutoCount: entry.bothExclusionAutoCount || 0,
    }))
  }, [summaryStats])

  const DeltaBadge = ({ current, previous, suffix = '' }: { current: number; previous: number; suffix?: string }) => {
    const diff = current - previous
    if (diff === 0) return <Badge size="xs" variant="light" color="gray" leftSection={<IconMinus size={10} />}>0{suffix}</Badge>
    const isPositive = diff > 0
    return (
      <Badge
        size="xs"
        variant="light"
        color={isPositive ? 'green' : 'red'}
        leftSection={isPositive ? <IconArrowUp size={10} /> : <IconArrowDown size={10} />}
      >
        {isPositive ? '+' : ''}{diff.toLocaleString()}{suffix}
      </Badge>
    )
  }

  if (isLoading) {
    return (
      <Center h={400}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Loading routing data...</Text>
        </Stack>
      </Center>
    )
  }

  return (
    <Container fluid p="md">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <div>
            <Title order={2}>Decision Routing</Title>
            <Text c="dimmed" size="sm">
              Analyzing offense routing and queue assignments
            </Text>
          </div>
          <Button leftSection={<IconRefresh size={16} />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Group>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group align="flex-end" gap="md">
            <DatePickerInput
              type="range"
              allowSingleDateInRange
              label="Search Date Range"
              placeholder="Select date range"
              value={dateRange}
              onChange={setDateRange}
              leftSection={<IconCalendar size={16} />}
              clearable
              style={{ flex: 1 }}
            />
            <Select
              label="Product"
              placeholder="All products"
              value={searchTypeFilter}
              onChange={setSearchTypeFilter}
              data={[
                { value: '__ALL__', label: 'All' },
                ...(stats?.availableSearchTypes?.map(t => ({ value: t, label: t })) || []),
              ]}
              clearable
              searchable
              style={{ minWidth: 200 }}
            />
            <Select
              label="Source"
              placeholder="All sources"
              value={sourceNormFilter}
              onChange={setSourceNormFilter}
              data={[
                { value: '__ALL__', label: 'All' },
                ...(stats?.availableSourceNorms?.map(s => ({ value: s, label: s })) || []),
              ]}
              clearable
              searchable
              style={{ minWidth: 200 }}
            />
            <Select
              label="Model Version"
              placeholder={globalVersion ? `Global: ${globalVersion}` : 'All versions'}
              value={pageVersionOverride}
              onChange={setPageVersionOverride}
              data={modelVersions.map(v => ({
                value: v.version,
                label: formatModelVersionLabel(v.version, v.isCurrent),
              }))}
              clearable
              searchable
              style={{ minWidth: 200 }}
            />
            {modelVersionFilter && selectedModelVersionInfo && (
              <Badge size="lg" variant="light" color="indigo" mt={24}>
                {selectedModelVersionInfo.runCount} run{selectedModelVersionInfo.runCount === 1 ? '' : 's'}
              </Badge>
            )}
          </Group>
        </Card>


        {/* Analytics sections - hidden for expert users */}
        {!isExpert && stats && (
          <>
            <Group justify="space-between" align="center">
              <Title order={3}>Routing Summary</Title>
              <Group gap="sm" align="center">
                {compareEnabled && (
                  <Select
                    placeholder="Compare mode"
                    value={compareMode}
                    onChange={(v) => setCompareMode((v as 'version' | 'chronological') || 'version')}
                    data={[
                      { value: 'version', label: 'Model versions' },
                      { value: 'chronological', label: 'Chronological runs' },
                    ]}
                    size="xs"
                    w={170}
                  />
                )}
                {compareEnabled && compareMode === 'version' && (
                  <>
                    <Select
                      placeholder="Version A"
                      value={compareV1}
                      onChange={setCompareV1}
                      data={modelVersions.map(v => ({
                        value: v.version,
                        label: formatModelVersionLabel(v.version, v.isCurrent),
                      }))}
                      size="xs"
                      w={140}
                      searchable
                    />
                    <Select
                      placeholder="Version B"
                      value={compareV2}
                      onChange={setCompareV2}
                      data={modelVersions.map(v => ({
                        value: v.version,
                        label: formatModelVersionLabel(v.version, v.isCurrent),
                      }))}
                      size="xs"
                      w={140}
                      searchable
                    />
                  </>
                )}
                <Button
                  variant={compareEnabled ? 'filled' : 'light'}
                  color="indigo"
                  size="xs"
                  leftSection={isComparisonFetching ? <Loader size={14} color="white" /> : <IconChartBar size={14} />}
                  onClick={() => setCompareEnabled(v => !v)}
                  disabled={isComparisonLoading && compareEnabled}
                >
                  {compareEnabled ? 'Hide Comparison' : 'Compare'}
                </Button>
              </Group>
            </Group>

            {compareEnabled && !comparison && isComparisonFetching && (
              <Paper p="md" radius="md" withBorder style={{ background: '#F0F4FF', borderColor: '#667EEA' }}>
                <Group justify="center" gap="sm">
                  <Loader size="sm" color="indigo" />
                  <Text size="sm" c="dimmed">Loading comparison data from BigQuery (this may take 15–30 seconds)...</Text>
                </Group>
              </Paper>
            )}

            {compareEnabled && isComparisonError && (
              <Paper p="md" radius="md" withBorder style={{ background: '#FFF5F5', borderColor: '#FC8181' }}>
                <Text size="sm" c="red">
                  Failed to load comparison data{(comparisonError as Error)?.message ? `: ${(comparisonError as Error).message}` : ''}.
                  Try narrowing the date range or retrying.
                </Text>
              </Paper>
            )}

            {compareEnabled && compareMode === 'version' && compareVersionsWithNoReplayData.length > 0 && (
              <Alert icon={<IconInfoCircle size={16} />} color="yellow" variant="light" title="No replay data for selected version">
                {compareVersionsWithNoReplayData.map(v => formatModelVersionLabel(v, modelVersions.find(m => m.version === v)?.isCurrent)).join(', ')}
                {' '}has no offense decisions in this date range yet. Run a replay after deploying before comparing.
              </Alert>
            )}

            {compareEnabled && isVersionComparison && versionCompareStats && (() => {
              const v1s = versionCompareStats.v1Stats
              const v2s = versionCompareStats.v2Stats
              const v1Remaining = v1s.remainingOffenses || 1
              const v2Remaining = v2s.remainingOffenses || 1
              const v1AiPct = v1Remaining > 0 ? ((v1s.aiAutomatedCount / v1Remaining) * 100).toFixed(0) : '0'
              const v2AiPct = v2Remaining > 0 ? ((v2s.aiAutomatedCount / v2Remaining) * 100).toFixed(0) : '0'
              return (
                <Card shadow="sm" padding="lg" radius="md" withBorder style={{ background: '#FAFBFF' }}>
                  <Stack gap="md">
                    <Group gap="xs" justify="space-between">
                      <Group gap="xs">
                        <IconChartBar size={18} color="#667EEA" />
                        <Text size="md" fw={700} c="#434190">
                          Version Comparison
                        </Text>
                      </Group>
                      <Badge variant="light" color="indigo" size="lg">{compareV1} → {compareV2}</Badge>
                    </Group>

                    {/* Headline stat */}
                    <Paper p="sm" radius="md" style={{ background: '#F0FFF4', border: '1px solid #C6F6D5' }}>
                      <Group justify="center" gap="xl">
                        <div style={{ textAlign: 'center' }}>
                          <Text size="xs" c="dimmed">{compareV1}</Text>
                          <Text size="lg" fw={700} c="#2ECC71">~{v1AiPct}% automated</Text>
                          <Text size="xs" c="dimmed">{fmt(v1s.aiAutomatedCount)} offenses</Text>
                        </div>
                        <Text size="xl" fw={700} c="dimmed">→</Text>
                        <div style={{ textAlign: 'center' }}>
                          <Text size="xs" c="dimmed">{compareV2}</Text>
                          <Text size="lg" fw={700} c="#2ECC71">~{v2AiPct}% automated</Text>
                          <Text size="xs" c="dimmed">{fmt(v2s.aiAutomatedCount)} offenses</Text>
                        </div>
                        <DeltaBadge current={v2s.aiAutomatedCount} previous={v1s.aiAutomatedCount} />
                      </Group>
                    </Paper>

                    {/* Side-by-side Accuracy cards */}
                    <Text size="sm" fw={600}>Routing Accuracy</Text>
                    <Grid>
                      <Grid.Col span={6}>
                        <Paper p="sm" radius="md" withBorder style={{ textAlign: 'center' }}>
                          <Text size="xs" c="dimmed" mb={4}>{compareV1}</Text>
                          <Text size="2rem" fw={700}>{v1s.accuracyPct.toFixed(1)}%</Text>
                          <Group justify="center" gap="lg" mt="xs">
                            <Text size="xs" c="dimmed">Auto: {v1s.automationAccuracyPct.toFixed(1)}%</Text>
                            <Text size="xs" c="dimmed">Auditor: {v1s.auditorAccuracyPct.toFixed(1)}%</Text>
                            <Text size="xs" c="dimmed">HI: {v1s.humanInterventionAccuracyPct.toFixed(1)}%</Text>
                          </Group>
                        </Paper>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Paper p="sm" radius="md" withBorder style={{ textAlign: 'center', background: '#F0F4FF' }}>
                          <Text size="xs" c="dimmed" mb={4}>{compareV2}</Text>
                          <Group justify="center" gap={4}>
                            <Text size="2rem" fw={700}>{v2s.accuracyPct.toFixed(1)}%</Text>
                            <DeltaBadge current={Math.round(v2s.accuracyPct * 10)} previous={Math.round(v1s.accuracyPct * 10)} suffix="%" />
                          </Group>
                          <Group justify="center" gap="lg" mt="xs">
                            <Group gap={2}><Text size="xs" c="dimmed">Auto: {v2s.automationAccuracyPct.toFixed(1)}%</Text><DeltaBadge current={Math.round(v2s.automationAccuracyPct * 10)} previous={Math.round(v1s.automationAccuracyPct * 10)} suffix="%" /></Group>
                            <Group gap={2}><Text size="xs" c="dimmed">Auditor: {v2s.auditorAccuracyPct.toFixed(1)}%</Text><DeltaBadge current={Math.round(v2s.auditorAccuracyPct * 10)} previous={Math.round(v1s.auditorAccuracyPct * 10)} suffix="%" /></Group>
                            <Group gap={2}><Text size="xs" c="dimmed">HI: {v2s.humanInterventionAccuracyPct.toFixed(1)}%</Text><DeltaBadge current={Math.round(v2s.humanInterventionAccuracyPct * 10)} previous={Math.round(v1s.humanInterventionAccuracyPct * 10)} suffix="%" /></Group>
                          </Group>
                        </Paper>
                      </Grid.Col>
                    </Grid>

                    {/* Side-by-side Processing Breakdown */}
                    <Text size="sm" fw={600}>Offense Processing (With AI)</Text>
                    <Grid>
                      <Grid.Col span={6}>
                        <Paper p="sm" radius="md" withBorder>
                          <Text size="xs" c="dimmed" mb="xs">{compareV1}</Text>
                          <Group gap="xs" justify="space-between">
                            <Group gap={6}><div style={{ width: 10, height: 10, borderRadius: 3, background: '#8B5CF6' }} /><Text size="xs">Exclusion</Text></Group>
                            <Text size="xs" fw={700}>{fmt(v1s.exclusionRulesCount)}</Text>
                          </Group>
                          <Group gap="xs" justify="space-between">
                            <Group gap={6}><div style={{ width: 10, height: 10, borderRadius: 3, background: '#2ECC71' }} /><Text size="xs">AI Automated</Text></Group>
                            <Text size="xs" fw={700}>{fmt(v1s.aiAutomatedCount)}</Text>
                          </Group>
                          <Group gap="xs" justify="space-between">
                            <Group gap={6}><div style={{ width: 10, height: 10, borderRadius: 3, background: '#E67E22' }} /><Text size="xs">Researcher</Text></Group>
                            <Text size="xs" fw={700}>{fmt(v1s.researcherCount)}</Text>
                          </Group>
                        </Paper>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Paper p="sm" radius="md" withBorder style={{ background: '#F0F4FF' }}>
                          <Text size="xs" c="dimmed" mb="xs">{compareV2}</Text>
                          <Group gap="xs" justify="space-between">
                            <Group gap={6}><div style={{ width: 10, height: 10, borderRadius: 3, background: '#8B5CF6' }} /><Text size="xs">Exclusion</Text></Group>
                            <Group gap={4}><Text size="xs" fw={700}>{fmt(v2s.exclusionRulesCount)}</Text><DeltaBadge current={v2s.exclusionRulesCount} previous={v1s.exclusionRulesCount} /></Group>
                          </Group>
                          <Group gap="xs" justify="space-between">
                            <Group gap={6}><div style={{ width: 10, height: 10, borderRadius: 3, background: '#2ECC71' }} /><Text size="xs">AI Automated</Text></Group>
                            <Group gap={4}><Text size="xs" fw={700}>{fmt(v2s.aiAutomatedCount)}</Text><DeltaBadge current={v2s.aiAutomatedCount} previous={v1s.aiAutomatedCount} /></Group>
                          </Group>
                          <Group gap="xs" justify="space-between">
                            <Group gap={6}><div style={{ width: 10, height: 10, borderRadius: 3, background: '#E67E22' }} /><Text size="xs">Researcher</Text></Group>
                            <Group gap={4}><Text size="xs" fw={700}>{fmt(v2s.researcherCount)}</Text><DeltaBadge current={v2s.researcherCount} previous={v1s.researcherCount} /></Group>
                          </Group>
                        </Paper>
                      </Grid.Col>
                    </Grid>
                  </Stack>
                </Card>
              )
            })()}

            {compareEnabled && chronologicalComparison && (
              <Paper p="sm" radius="md" withBorder style={{ background: '#F0F4FF', borderColor: '#667EEA' }}>
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconChartBar size={16} color="#667EEA" />
                    <Text size="sm" fw={600} c="#434190">
                      Run Comparison — {fmt(chronologicalComparison.offensesWithMultiRuns)} offenses re-evaluated ({fmt(chronologicalComparison.offensesWith3PlusRuns)} with 3+ runs)
                    </Text>
                  </Group>
                  <Group gap="lg">
                    <Text size="xs" c="dimmed">
                      Decision changes (latest vs previous): <Text span fw={700} c={chronologicalComparison.changedLatestVsPrev > 0 ? 'orange' : 'green'}>{fmt(chronologicalComparison.changedLatestVsPrev)}</Text>
                    </Text>
                    <Text size="xs" c="dimmed">
                      Decision changes (prev vs 2-ago): <Text span fw={700} c={chronologicalComparison.changedPrevVsPrev2 > 0 ? 'orange' : 'green'}>{fmt(chronologicalComparison.changedPrevVsPrev2)}</Text>
                    </Text>
                  </Group>
                </Stack>
              </Paper>
            )}


            {/* Overall Distribution */}
            <Stack gap="xs">
              <Text size="lg" fw={600}>Overall Distribution</Text>
              <Grid>
                <Grid.Col span={4}>
                  <Card shadow="sm" padding="lg" radius="md" style={{ background: '#4A5568', color: 'white', height: '100%' }}>
                    <Stack align="center" gap="xs">
                      <Text size="3rem" fw={700}>{fmt(summaryStats?.totalOffenses)}</Text>
                      <Text size="sm">Total Offenses</Text>
                      {compareEnabled && isVersionComparison && versionCompareStats && (
                        <Group gap={4}>
                          <Text size="xs" style={{ opacity: 0.8 }}>vs {compareV1}:</Text>
                          <DeltaBadge current={versionCompareStats.v2Stats.totalOffenses} previous={versionCompareStats.v1Stats.totalOffenses} />
                        </Group>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Card shadow="sm" padding="lg" radius="md" style={{ background: '#E74C3C', color: 'white', height: '100%' }}>
                    <Stack align="center" gap="xs">
                      <Text size="3rem" fw={700}>{fmt(summaryStats?.ignoredOffenses)}</Text>
                      <Text size="sm">
                        Ignored by UBS ({((summaryStats?.ignoredOffenses ?? 0) / (summaryStats?.totalOffenses || 1) * 100).toFixed(1)}%)
                      </Text>
                      {compareEnabled && isVersionComparison && versionCompareStats && (
                        <Group gap={4}>
                          <Text size="xs" style={{ opacity: 0.8 }}>vs {compareV1}:</Text>
                          <DeltaBadge current={versionCompareStats.v2Stats.ignoredOffenses} previous={versionCompareStats.v1Stats.ignoredOffenses} />
                        </Group>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Card shadow="sm" padding="lg" radius="md" style={{ background: '#2ECC71', color: 'white', height: '100%' }}>
                    <Stack align="center" gap="xs">
                      <Text size="3rem" fw={700}>{fmt(summaryStats?.remainingOffenses)}</Text>
                      <Text size="sm">
                        Remaining ({((summaryStats?.remainingOffenses ?? 0) / (summaryStats?.totalOffenses || 1) * 100).toFixed(1)}%)
                      </Text>
                      {compareEnabled && isVersionComparison && versionCompareStats && (
                        <Group gap={4}>
                          <Text size="xs" style={{ opacity: 0.8 }}>vs {compareV1}:</Text>
                          <DeltaBadge current={versionCompareStats.v2Stats.remainingOffenses} previous={versionCompareStats.v1Stats.remainingOffenses} />
                        </Group>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>
              </Grid>
              <Paper p="xs" radius="md" withBorder style={{ background: '#EBF8FF', borderColor: '#3182CE' }}>
                <Group gap="xs">
                  <IconInfoCircle size={16} color="#3182CE" />
                  <Text size="xs" c="#2C5282">
                    Ignored offenses have ID match issues (id_match_validated != 'ID_MATCH') or are duplicates (duplicate = 'YES')
                  </Text>
                </Group>
              </Paper>
            </Stack>

            {/* Queue Distribution (Remaining Offenses) */}
            <Stack gap="xs">
              <Text size="lg" fw={600}>Queue Distribution (Remaining Offenses)</Text>
              <Grid>
                <Grid.Col span={4}>
                  <Card shadow="sm" padding="lg" radius="md" style={{ background: '#2ECC71', color: 'white', height: '100%' }}>
                    <Stack align="center" gap="xs">
                      <Text size="3rem" fw={700}>{fmt(summaryStats?.automationCount)}</Text>
                      <Text size="sm">
                        Automation ({(((summaryStats?.automationCount ?? 0) / (summaryStats?.remainingOffenses || 1)) * 100).toFixed(1)}%)
                      </Text>
                      {compareEnabled && isVersionComparison && versionCompareStats && (
                        <Group gap={4}>
                          <Text size="xs" style={{ opacity: 0.8 }}>vs {compareV1}:</Text>
                          <DeltaBadge current={versionCompareStats.v2Stats.automationCount} previous={versionCompareStats.v1Stats.automationCount} />
                        </Group>
                      )}
                      {compareEnabled && chronologicalComparison && (
                        <Group gap={4}>
                          <Text size="xs" style={{ opacity: 0.8 }}>vs prev:</Text>
                          <DeltaBadge current={chronologicalComparison.latestRun.automation} previous={chronologicalComparison.previousRun.automation} />
                        </Group>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Card shadow="sm" padding="lg" radius="md" style={{ background: '#F39C12', color: 'white', height: '100%' }}>
                    <Stack align="center" gap="xs">
                      <Text size="3rem" fw={700}>{fmt(summaryStats?.auditorCount)}</Text>
                      <Text size="sm">
                        Auditor ({(((summaryStats?.auditorCount ?? 0) / (summaryStats?.remainingOffenses || 1)) * 100).toFixed(1)}%)
                      </Text>
                      {compareEnabled && isVersionComparison && versionCompareStats && (
                        <Group gap={4}>
                          <Text size="xs" style={{ opacity: 0.8 }}>vs {compareV1}:</Text>
                          <DeltaBadge current={versionCompareStats.v2Stats.auditorCount} previous={versionCompareStats.v1Stats.auditorCount} />
                        </Group>
                      )}
                      {compareEnabled && chronologicalComparison && (
                        <Group gap={4}>
                          <Text size="xs" style={{ opacity: 0.8 }}>vs prev:</Text>
                          <DeltaBadge current={chronologicalComparison.latestRun.auditor} previous={chronologicalComparison.previousRun.auditor} />
                        </Group>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Card shadow="sm" padding="lg" radius="md" style={{ background: '#E67E22', color: 'white', height: '100%' }}>
                    <Stack align="center" gap="xs">
                      <Text size="3rem" fw={700}>{fmt(summaryStats?.insufficientDataCount)}</Text>
                      <Text size="sm">
                        Human Intervention ({(((summaryStats?.insufficientDataCount ?? 0) / (summaryStats?.remainingOffenses || 1)) * 100).toFixed(1)}%)
                      </Text>
                      {compareEnabled && isVersionComparison && versionCompareStats && (
                        <Group gap={4}>
                          <Text size="xs" style={{ opacity: 0.8 }}>vs {compareV1}:</Text>
                          <DeltaBadge current={versionCompareStats.v2Stats.humanInterventionCount} previous={versionCompareStats.v1Stats.humanInterventionCount} />
                        </Group>
                      )}
                      {compareEnabled && chronologicalComparison && (
                        <Group gap={4}>
                          <Text size="xs" style={{ opacity: 0.8 }}>vs prev:</Text>
                          <DeltaBadge current={chronologicalComparison.latestRun.humanIntervention} previous={chronologicalComparison.previousRun.humanIntervention} />
                        </Group>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>
              </Grid>
            </Stack>

            {/* Run Comparison Detail Table */}
            {compareEnabled && chronologicalComparison && (
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Text size="lg" fw={600}>Run-over-Run Comparison (Last 3 Runs)</Text>
                  <Paper p="xs" radius="md" withBorder style={{ background: '#FFFFF0', borderColor: '#D69E2E' }}>
                    <Group gap="xs">
                      <IconInfoCircle size={16} color="#D69E2E" />
                      <Text size="xs" c="#744210">
                        For offenses with fewer runs, earlier run values are carried forward. Deltas reflect actual re-evaluation changes only.
                      </Text>
                    </Group>
                  </Paper>
                  <ScrollArea>
                    <Table striped highlightOnHover withTableBorder withColumnBorders>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Metric</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Latest Run</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Previous Run</Table.Th>
                          <Table.Th style={{ textAlign: 'center' }}>Delta (L vs P)</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>2 Runs Ago</Table.Th>
                          <Table.Th style={{ textAlign: 'center' }}>Delta (P vs 2A)</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {[
                          { label: 'Reportable', key: 'reportable' as const },
                          { label: 'Not Reportable', key: 'notReportable' as const },
                          { label: 'Other (Review/Manual)', key: 'other' as const },
                          { label: 'Est. Automation Queue', key: 'automation' as const },
                          { label: 'Est. Auditor Queue', key: 'auditor' as const },
                          { label: 'Est. Human Intervention', key: 'humanIntervention' as const },
                          { label: 'Accuracy vs UBS', key: 'accuracy' as const },
                        ].map(({ label, key }) => {
                          const isAccuracy = key === 'accuracy'
                          const fmtVal = (v: number) => isAccuracy ? `${v.toFixed(1)}%` : fmt(v)
                          const l = chronologicalComparison.latestRun[key]
                          const p = chronologicalComparison.previousRun[key]
                          const t = chronologicalComparison.twoRunsAgo[key]
                          return (
                            <Table.Tr key={key}>
                              <Table.Td><Text size="sm" fw={500}>{label}</Text></Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}><Text size="sm" fw={700}>{fmtVal(l)}</Text></Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{fmtVal(p)}</Text></Table.Td>
                              <Table.Td style={{ textAlign: 'center' }}>
                                <DeltaBadge current={l} previous={p} suffix={isAccuracy ? '%' : ''} />
                              </Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{fmtVal(t)}</Text></Table.Td>
                              <Table.Td style={{ textAlign: 'center' }}>
                                <DeltaBadge current={p} previous={t} suffix={isAccuracy ? '%' : ''} />
                              </Table.Td>
                            </Table.Tr>
                          )
                        })}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>
                </Stack>
              </Card>
            )}

            {/* Human Intervention Breakdown — 3 groups */}
            {(summaryStats?.insufficientDataCount ?? 0) > 0 && summaryStats?.insufficientDataBreakdown && (() => {
              const raw = summaryStats.insufficientDataBreakdown
              const num = (v: unknown) => {
                const n = Number(v)
                return Number.isFinite(n) ? n : 0
              }
              const bd = {
                ...raw,
                adjudicationBanned: num(raw.adjudicationBanned),
                manualReview: num(raw.manualReview),
                dispositionInProgress: num(raw.dispositionInProgress),
                ambiguousClassification: num(raw.ambiguousClassification),
                validationFailed: num(raw.validationFailed),
                reportabilityUnclear: num((raw as { reportabilityUnclear?: unknown }).reportabilityUnclear),
                idOnly: num(raw.idOnly),
                other: num(raw.other),
              }
              const total = bd.adjudicationBanned + bd.manualReview + bd.dispositionInProgress + bd.ambiguousClassification + bd.validationFailed + bd.reportabilityUnclear + bd.idOnly + bd.other
              const researchRequired = bd.adjudicationBanned + bd.manualReview + bd.dispositionInProgress
              const insufficientData = bd.validationFailed + bd.reportabilityUnclear + bd.idOnly + bd.other
              const pct = (n: number) => {
                if (!total || !Number.isFinite(n)) return '0.0'
                return ((n / total) * 100).toFixed(1)
              }

              return (
                <>
                  <Title order={3}>📋 Human Intervention Breakdown</Title>
                  <Card shadow="sm" padding="lg" radius="md" withBorder>
                    <Stack gap="md">
                      <Paper p="xs" radius="md" withBorder style={{ background: '#EBF8FF', borderColor: '#3182CE' }}>
                        <Group gap="xs">
                          <IconInfoCircle size={16} color="#3182CE" />
                          <Text size="xs" c="#2C5282">
                            Mutually exclusive — each offense is counted in exactly one bucket. Total: {total.toLocaleString()}
                          </Text>
                        </Group>
                      </Paper>

                      {/* GROUP 1: Research Required */}
                      <Paper p="md" withBorder style={{ borderLeft: '4px solid #E53E3E' }}>
                        <Stack gap="sm">
                          <Group gap="xs" align="baseline">
                            <Text size="lg" fw={700}>Research Required</Text>
                            <Text size="xl" fw={700} c="red">{researchRequired.toLocaleString()}</Text>
                            <Text size="sm" c="dimmed">({pct(researchRequired)}%)</Text>
                          </Group>
                          <Text size="xs" c="dimmed">Banned adjudication, in-progress cases, and AI manual review flags</Text>
                          <Grid>
                            <Grid.Col span={4}>
                              <Stack gap={2}>
                                <Text size="xs" c="dimmed">Adjudication Banned</Text>
                                <Group gap={4} align="baseline">
                                  <Text size="md" fw={700} c="red">{fmt(bd.adjudicationBanned)}</Text>
                                  <Text size="xs" c="dimmed">({pct(bd.adjudicationBanned)}%)</Text>
                                </Group>
                              </Stack>
                            </Grid.Col>
                            <Grid.Col span={4}>
                              <Stack gap={2}>
                                <Text size="xs" c="dimmed">AI Manual Review</Text>
                                <Group gap={4} align="baseline">
                                  <Text size="md" fw={700} c="red">{fmt(bd.manualReview)}</Text>
                                  <Text size="xs" c="dimmed">({pct(bd.manualReview)}%)</Text>
                                </Group>
                              </Stack>
                            </Grid.Col>
                            <Grid.Col span={4}>
                              <Stack gap={2}>
                                <Text size="xs" c="dimmed">Disposition In Progress</Text>
                                <Group gap={4} align="baseline">
                                  <Text size="md" fw={700} c="red">{fmt(bd.dispositionInProgress)}</Text>
                                  <Text size="xs" c="dimmed">({pct(bd.dispositionInProgress)}%)</Text>
                                </Group>
                              </Stack>
                            </Grid.Col>
                          </Grid>
                          {bd.adjudicationBannedReasons && bd.adjudicationBannedReasons.length > 0 && (
                            <Paper p="sm" radius="sm" style={{ background: '#FFF5F5', border: '1px solid #FEB2B2' }}>
                              <Text size="xs" fw={600} c="dimmed" mb={6}>Adjudication Banned items:</Text>
                              <Stack gap={4}>
                                {bd.adjudicationBannedReasons.map((item: { reason: string; count: number }, idx: number) => (
                                  <Group key={idx} justify="space-between" gap="xs">
                                    <Text size="xs" style={{ flex: 1 }}>{item.reason}</Text>
                                    <Text size="xs" fw={700} c="red">{item.count.toLocaleString()}</Text>
                                  </Group>
                                ))}
                              </Stack>
                            </Paper>
                          )}
                        </Stack>
                      </Paper>

                      {/* GROUP 2: Insufficient Data */}
                      <Paper p="md" withBorder style={{ borderLeft: '4px solid #3182CE' }}>
                        <Stack gap="sm">
                          <Group gap="xs" align="baseline">
                            <Text size="lg" fw={700}>Insufficient Data</Text>
                            <Text size="xl" fw={700} c="blue">{insufficientData.toLocaleString()}</Text>
                            <Text size="sm" c="dimmed">({pct(insufficientData)}%)</Text>
                          </Group>
                          <Text size="xs" c="dimmed">Validation failures, reportability/identity signals, and other</Text>
                          <Grid>
                            <Grid.Col span={3}>
                              <Stack gap={2}>
                                <Text size="xs" c="dimmed">Validation Failed</Text>
                                <Group gap={4} align="baseline">
                                  <Text size="md" fw={700} c="blue">{fmt(bd.validationFailed)}</Text>
                                  <Text size="xs" c="dimmed">({pct(bd.validationFailed)}%)</Text>
                                </Group>
                              </Stack>
                            </Grid.Col>
                            <Grid.Col span={3}>
                              <Stack gap={2}>
                                <Text size="xs" c="dimmed">Reportability Unclear</Text>
                                <Group gap={4} align="baseline">
                                  <Text size="md" fw={700} c="blue">{fmt(bd.reportabilityUnclear)}</Text>
                                  <Text size="xs" c="dimmed">({pct(bd.reportabilityUnclear)}%)</Text>
                                </Group>
                              </Stack>
                            </Grid.Col>
                            <Grid.Col span={3}>
                              <Stack gap={2}>
                                <Text size="xs" c="dimmed">ID Issue Only</Text>
                                <Group gap={4} align="baseline">
                                  <Text size="md" fw={700} c="blue">{fmt(bd.idOnly)}</Text>
                                  <Text size="xs" c="dimmed">({pct(bd.idOnly)}%)</Text>
                                </Group>
                              </Stack>
                            </Grid.Col>
                            <Grid.Col span={3}>
                              <Stack gap={2}>
                                <Text size="xs" c="dimmed">Other</Text>
                                <Group gap={4} align="baseline">
                                  <Text size="md" fw={700} c="gray">{fmt(bd.other)}</Text>
                                  <Text size="xs" c="dimmed">({pct(bd.other)}%)</Text>
                                </Group>
                              </Stack>
                            </Grid.Col>
                          </Grid>

                          {/* ID Only sub-analysis */}
                          {bd.idOnlyAnalysis && bd.idOnlyAnalysis.total > 0 && (() => {
                            const a = bd.idOnlyAnalysis
                            const rpct = (n: number) => a.total > 0 ? ((n / a.total) * 100).toFixed(1) : '0.0'
                            const accuracy = a.total > 0 ? ((a.matches / a.total) * 100).toFixed(1) : '0.0'
                            return (
                              <Paper p="sm" radius="sm" style={{ background: '#F7FAFC', border: '1px solid #E2E8F0' }}>
                                <Text size="xs" fw={600} c="dimmed" mb={6}>ID Issue Only — CE Reportability &amp; Accuracy ({fmt(a.total)} offenses):</Text>
                                <Grid>
                                  <Grid.Col span={6}>
                                    <Grid>
                                      <Grid.Col span={4}>
                                        <Stack gap={2}>
                                          <Text size="xs" c="dimmed">Reportable</Text>
                                          <Group gap={4} align="baseline">
                                            <Text size="md" fw={700} c="red">{fmt(a.reportable)}</Text>
                                            <Text size="xs" c="dimmed">({rpct(a.reportable)}%)</Text>
                                          </Group>
                                        </Stack>
                                      </Grid.Col>
                                      <Grid.Col span={4}>
                                        <Stack gap={2}>
                                          <Text size="xs" c="dimmed">Not Reportable</Text>
                                          <Group gap={4} align="baseline">
                                            <Text size="md" fw={700} c="green">{fmt(a.notReportable)}</Text>
                                            <Text size="xs" c="dimmed">({rpct(a.notReportable)}%)</Text>
                                          </Group>
                                        </Stack>
                                      </Grid.Col>
                                      <Grid.Col span={4}>
                                        <Stack gap={2}>
                                          <Text size="xs" c="dimmed">Not Enough Info*</Text>
                                          <Group gap={4} align="baseline">
                                            <Text size="md" fw={700} c="gray">{fmt(a.notEnoughInfo)}</Text>
                                            <Text size="xs" c="dimmed">({rpct(a.notEnoughInfo)}%)</Text>
                                          </Group>
                                        </Stack>
                                      </Grid.Col>
                                    </Grid>
                                    <Text size="xs" c="dimmed" mt={4} fs="italic">* Missing jurisdiction state, including federal searches</Text>
                                  </Grid.Col>
                                  <Grid.Col span={6}>
                                    <Grid>
                                      <Grid.Col span={4}>
                                        <Stack gap={2}>
                                          <Text size="xs" c="dimmed">Accuracy</Text>
                                          <Text size="md" fw={700} c="blue">{accuracy}%</Text>
                                        </Stack>
                                      </Grid.Col>
                                      <Grid.Col span={4}>
                                        <Stack gap={2}>
                                          <Text size="xs" c="dimmed">Matches</Text>
                                          <Group gap={4} align="baseline">
                                            <Text size="md" fw={700} c="green">{fmt(a.matches)}</Text>
                                            <Text size="xs" c="dimmed">({rpct(a.matches)}%)</Text>
                                          </Group>
                                        </Stack>
                                      </Grid.Col>
                                      <Grid.Col span={4}>
                                        <Stack gap={2}>
                                          <Text size="xs" c="dimmed">Mismatches</Text>
                                          <Group gap={4} align="baseline">
                                            <Text size="md" fw={700} c="red">{fmt(a.mismatches)}</Text>
                                            <Text size="xs" c="dimmed">({rpct(a.mismatches)}%)</Text>
                                          </Group>
                                        </Stack>
                                      </Grid.Col>
                                    </Grid>
                                  </Grid.Col>
                                </Grid>
                              </Paper>
                            )
                          })()}

                          {/* Validation Failed sub-analyses */}
                          {bd.validationReasons && bd.validationReasons.length > 0 && (() => {
                            const lowEnsembleTotal = bd.validationReasons
                              .filter(vr => vr.reason.startsWith('Low ensemble confidence'))
                              .reduce((s, vr) => s + vr.count, 0)
                            const lowCrimeConfTotal = bd.validationReasons
                              .filter(vr => vr.reason.startsWith('Crime category confidence is low'))
                              .reduce((s, vr) => s + vr.count, 0)
                            const otherReasons = bd.validationReasons.filter(vr => !vr.reason.startsWith('Low ensemble confidence') && !vr.reason.startsWith('Crime category confidence is low'))
                            const combinedReasons = [
                              ...otherReasons,
                              ...(lowEnsembleTotal > 0 ? [{ reason: 'Low ensemble confidence', count: lowEnsembleTotal }] : []),
                              ...(lowCrimeConfTotal > 0 ? [{ reason: 'Crime category confidence is low', count: lowCrimeConfTotal }] : []),
                            ].sort((a, b) => b.count - a.count)
                            return (
                              <Paper p="sm" radius="sm" style={{ background: '#F7FAFC', border: '1px solid #E2E8F0' }}>
                                <Text size="xs" fw={600} c="dimmed" mb={6}>Validation Failed — issues (an offense can have multiple):</Text>
                                <Stack gap={4}>
                                  {combinedReasons.map((vr, idx) => (
                                    <Group key={idx} justify="space-between" gap="xs">
                                      <Text size="xs" style={{ flex: 1 }}>{vr.reason}</Text>
                                      <Text size="xs" fw={700} c="orange">{vr.count.toLocaleString()}</Text>
                                    </Group>
                                  ))}
                                </Stack>
                              </Paper>
                            )
                          })()}
                          {bd.validationFailedOffenses && bd.validationFailedOffenses.total > 0 && (() => {
                            const vo = bd.validationFailedOffenses
                            const vpct = (n: number) => vo.total > 0 ? ((n / vo.total) * 100).toFixed(1) : '0.0'
                            return (
                              <Paper p="sm" radius="sm" style={{ background: '#FFFAF0', border: '1px solid #ED8936' }}>
                                <Text size="xs" fw={600} c="dimmed" mb={6}>Validation Failed — offense categories, mutually exclusive ({fmt(vo.total)}):</Text>
                                <Grid>
                                  <Grid.Col span={3}>
                                    <Stack gap={2}>
                                      <Text size="xs" c="dimmed">Dismissed</Text>
                                      <Group gap={4} align="baseline">
                                        <Text size="md" fw={700} c="orange">{fmt(vo.dismissed)}</Text>
                                        <Text size="xs" c="dimmed">({vpct(vo.dismissed)}%)</Text>
                                      </Group>
                                    </Stack>
                                  </Grid.Col>
                                  <Grid.Col span={3}>
                                    <Stack gap={2}>
                                      <Text size="xs" c="dimmed">Infraction</Text>
                                      <Group gap={4} align="baseline">
                                        <Text size="md" fw={700} c="orange">{fmt(vo.infraction)}</Text>
                                        <Text size="xs" c="dimmed">({vpct(vo.infraction)}%)</Text>
                                      </Group>
                                    </Stack>
                                  </Grid.Col>
                                  <Grid.Col span={3}>
                                    <Stack gap={2}>
                                      <Text size="xs" c="dimmed">Traffic</Text>
                                      <Group gap={4} align="baseline">
                                        <Text size="md" fw={700} c="orange">{fmt(vo.traffic)}</Text>
                                        <Text size="xs" c="dimmed">({vpct(vo.traffic)}%)</Text>
                                      </Group>
                                    </Stack>
                                  </Grid.Col>
                                  <Grid.Col span={3}>
                                    <Stack gap={2}>
                                      <Text size="xs" c="dimmed">Other</Text>
                                      <Group gap={4} align="baseline">
                                        <Text size="md" fw={700} c="gray">{fmt(vo.other)}</Text>
                                        <Text size="xs" c="dimmed">({vpct(vo.other)}%)</Text>
                                      </Group>
                                    </Stack>
                                  </Grid.Col>
                                </Grid>
                              </Paper>
                            )
                          })()}
                        </Stack>
                      </Paper>

                      {/* GROUP 3: Ambiguous Classification */}
                      <Paper p="md" withBorder style={{ borderLeft: '4px solid #ED8936' }}>
                        <Stack gap="sm">
                          <Group gap="xs" align="baseline">
                            <Text size="lg" fw={700}>Ambiguous Classification</Text>
                            <Text size="xl" fw={700} c="orange">{fmt(bd.ambiguousClassification)}</Text>
                            <Text size="sm" c="dimmed">({pct(bd.ambiguousClassification)}%)</Text>
                          </Group>
                          <Text size="xs" c="dimmed">Offenses with mismatch warnings (disposition class, type class, or crime category) or missing/indeterminate classifications, and no other validation errors or ID issues</Text>
                          {bd.ambiguousReasons && bd.ambiguousReasons.length > 0 && (() => {
                            const lowCrimeConfTotal = bd.ambiguousReasons
                              .filter(ar => ar.reason.startsWith('Crime category confidence is low'))
                              .reduce((s, ar) => s + ar.count, 0)
                            const otherReasons = bd.ambiguousReasons.filter(ar => !ar.reason.startsWith('Crime category confidence is low'))
                            const combinedReasons = [
                              ...otherReasons,
                              ...(lowCrimeConfTotal > 0 ? [{ reason: 'Crime category confidence is low', count: lowCrimeConfTotal }] : []),
                            ].sort((a, b) => b.count - a.count)
                            return (
                              <Paper p="sm" radius="sm" style={{ background: '#FFFAF0', border: '1px solid #ED8936' }}>
                                <Text size="xs" fw={600} c="dimmed" mb={6}>Mismatch warnings (an offense can have multiple):</Text>
                                <Stack gap={4}>
                                  {combinedReasons.map((ar, idx) => (
                                    <Group key={idx} justify="space-between" gap="xs">
                                      <Text size="xs" style={{ flex: 1 }}>{ar.reason}</Text>
                                      <Text size="xs" fw={700} c="orange">{ar.count.toLocaleString()}</Text>
                                    </Group>
                                  ))}
                                </Stack>
                              </Paper>
                            )
                          })()}
                        </Stack>
                      </Paper>

                    </Stack>
                  </Card>
                </>
              )
            })()}

            {/* Offense Breakdown Chart */}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text fw={600} size="lg" mb="lg">Offense Processing Breakdown</Text>
              {(() => {
                // Queue-based, mutually exclusive segments. Every offense lands
                // in exactly one routing queue (Automation / Auditor /
                // Insufficient Data). The exclusion-rule overlap is carved out
                // into its own purple segment, so the four pieces sum to total.
                const totalAuto = heatmapData.reduce((s, d) => s + d.autoCount, 0)
                const totalAuditor = heatmapData.reduce((s, d) => s + d.auditorCount, 0)
                const totalInsuff = heatmapData.reduce((s, d) => s + d.insuffCount, 0)
                const exclAuto = heatmapData.reduce((s, d) => s + d.exclusionAutoCount, 0)
                const exclAuditor = heatmapData.reduce((s, d) => s + d.exclusionAuditorCount, 0)
                const exclInsuff = heatmapData.reduce((s, d) => s + d.exclusionInsuffCount, 0)

                const exclusion = exclAuto + exclAuditor + exclInsuff
                const ai = totalAuto - exclAuto
                const auditor = totalAuditor - exclAuditor
                const researcher = totalInsuff - exclInsuff
                const total = exclusion + ai + auditor + researcher

                // Current UBS (pre-AI): exclusion rules vs. everything else
                const curExclusion = exclusion
                const curResearcher = total - curExclusion
                const curExclPct = total > 0 ? ((curExclusion / total) * 100).toFixed(1) : '0.0'
                const curResPct = total > 0 ? ((curResearcher / total) * 100).toFixed(1) : '0.0'

                const aiExclPct = total > 0 ? ((exclusion / total) * 100).toFixed(1) : '0.0'
                const aiPct = total > 0 ? ((ai / total) * 100).toFixed(1) : '0.0'
                const auditorPct = total > 0 ? ((auditor / total) * 100).toFixed(1) : '0.0'
                const aiResPct = total > 0 ? ((researcher / total) * 100).toFixed(1) : '0.0'

                // "Handled by AI" = both AI-automated and auditor-routed offenses
                // no longer require a researcher to triage them from scratch.
                const aiHandled = ai + auditor
                const automatedPct = curResearcher > 0 ? ((aiHandled / curResearcher) * 100).toFixed(0) : '0'

                return (
                  <Stack gap="xl">
                    <Group gap="xl" justify="center">
                      <div style={{ textAlign: 'center' }}>
                        <Text size="2.5rem" fw={800} lh={1}>{total.toLocaleString()}</Text>
                        <Text size="sm" c="dimmed" mt={4}>Total Offenses</Text>
                      </div>
                    </Group>

                    {/* Current UBS */}
                    <div>
                      <Text size="sm" fw={700} mb={8}>Current UBS</Text>
                      <div style={{ height: '40px', borderRadius: '8px', overflow: 'hidden', display: 'flex', border: '1px solid #E2E8F0' }}>
                        <div style={{ width: `${curExclPct}%`, background: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>
                          {curExclPct}%
                        </div>
                        <div style={{ width: `${curResPct}%`, background: '#E67E22', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>
                          {curResPct}%
                        </div>
                      </div>
                      <Group gap="xl" justify="center" mt={8}>
                        <Group gap={8}>
                          <div style={{ width: 14, height: 14, borderRadius: 4, background: '#8B5CF6' }} />
                          <div>
                            <Text size="sm" fw={700}>{curExclusion.toLocaleString()}</Text>
                            <Text size="xs" c="dimmed">Exclusion Rules ({curExclPct}%)</Text>
                          </div>
                        </Group>
                        <Group gap={8}>
                          <div style={{ width: 14, height: 14, borderRadius: 4, background: '#E67E22' }} />
                          <div>
                            <Text size="sm" fw={700}>{curResearcher.toLocaleString()}</Text>
                            <Text size="xs" c="dimmed">Researcher ({curResPct}%)</Text>
                          </div>
                        </Group>
                      </Group>
                    </div>

                    {/* With AI */}
                    <div>
                      <Text size="sm" fw={700} mb={8}>With AI Automation</Text>
                      <div style={{ height: '40px', borderRadius: '8px', overflow: 'hidden', display: 'flex', border: '1px solid #E2E8F0' }}>
                        <div style={{ width: `${aiExclPct}%`, background: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>
                          {aiExclPct}%
                        </div>
                        <div style={{ width: `${aiPct}%`, background: '#2ECC71', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>
                          {aiPct}%
                        </div>
                        <div style={{ width: `${auditorPct}%`, background: '#3182CE', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>
                          {auditorPct}%
                        </div>
                        <div style={{ width: `${aiResPct}%`, background: '#E67E22', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>
                          {aiResPct}%
                        </div>
                      </div>
                      <Group gap="xl" justify="center" mt={8}>
                        <Group gap={8}>
                          <div style={{ width: 14, height: 14, borderRadius: 4, background: '#8B5CF6' }} />
                          <div>
                            <Text size="sm" fw={700}>{exclusion.toLocaleString()}</Text>
                            <Text size="xs" c="dimmed">Exclusion Rules ({aiExclPct}%)</Text>
                          </div>
                        </Group>
                        <Group gap={8}>
                          <div style={{ width: 14, height: 14, borderRadius: 4, background: '#2ECC71' }} />
                          <div>
                            <Text size="sm" fw={700}>{ai.toLocaleString()}</Text>
                            <Text size="xs" c="dimmed">AI Automated ({aiPct}%)</Text>
                          </div>
                        </Group>
                        <Group gap={8}>
                          <div style={{ width: 14, height: 14, borderRadius: 4, background: '#3182CE' }} />
                          <div>
                            <Text size="sm" fw={700}>{auditor.toLocaleString()}</Text>
                            <Text size="xs" c="dimmed">Auditor ({auditorPct}%)</Text>
                          </div>
                        </Group>
                        <Group gap={8}>
                          <div style={{ width: 14, height: 14, borderRadius: 4, background: '#E67E22' }} />
                          <div>
                            <Text size="sm" fw={700}>{researcher.toLocaleString()}</Text>
                            <Text size="xs" c="dimmed">Researcher ({aiResPct}%)</Text>
                          </div>
                        </Group>
                      </Group>
                    </div>

                    {/* Summary */}
                    <div style={{ textAlign: 'center', padding: '12px 16px', background: '#F0FFF4', borderRadius: '8px', border: '1px solid #C6F6D5' }}>
                      <Text size="sm" fw={700} c="#2ECC71">
                        AI handles ~{automatedPct}% of researcher workload — {aiHandled.toLocaleString()} offenses (Automation + Auditor) no longer need manual triage
                      </Text>
                    </div>
                  </Stack>
                )
              })()}
            </Card>

            {/* 2x2 Confidence Matrix */}
            <Title order={3}>Routing Confidence Matrix</Title>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Text fw={600} size="lg">Queue Distribution</Text>
                <Text size="xs" c="dimmed">
                  One card per routing queue (Automation / Auditor / Human Intervention). Each queue shows how many of its offenses were cleared by exclusion rules vs. the rest.
                </Text>
                {stats && (() => {
                  // Pure queue breakdown. Every remaining (non-ignored) offense
                  // sits in exactly one routing queue, so the three totals sum to
                  // grandTotal. Each card keeps a note-based exclusion-rule split.
                  const sumOf = (sel: (d: typeof heatmapData[number]) => number) =>
                    heatmapData.reduce((s, d) => s + sel(d), 0)

                  const queues = [
                    {
                      key: 'automation',
                      label: 'Automation',
                      desc: 'Offenses auto-cleared by the routing engine',
                      color: '#2ECC71',
                      bg: '#F0FFF4',
                      total: sumOf(d => d.autoCount),
                      excl: sumOf(d => d.exclusionAutoCount),
                    },
                    {
                      key: 'auditor',
                      label: 'Auditor',
                      desc: 'Reportable offenses routed to an auditor',
                      color: '#3182CE',
                      bg: '#EBF8FF',
                      total: sumOf(d => d.auditorCount),
                      excl: sumOf(d => d.exclusionAuditorCount),
                    },
                    {
                      key: 'human',
                      label: 'Human Intervention',
                      desc: 'Insufficient Data — needs human review',
                      color: '#E67E22',
                      bg: '#FFF5F0',
                      total: sumOf(d => d.insuffCount),
                      excl: sumOf(d => d.exclusionInsuffCount),
                    },
                  ]
                  const grandTotal = queues.reduce((s, q) => s + q.total, 0)

                  return (
                    <Stack gap={8}>
                      {queues.map(q => {
                        const other = q.total - q.excl
                        const pct = grandTotal > 0 ? ((q.total / grandTotal) * 100).toFixed(1) : '0.0'
                        const exclPct = q.total > 0 ? ((q.excl / q.total) * 100).toFixed(1) : '0.0'
                        const otherPct = q.total > 0 ? ((other / q.total) * 100).toFixed(1) : '0.0'
                        return (
                          <div key={q.key} style={{ padding: '16px', background: q.bg, border: `2px solid ${q.color}`, borderRadius: '8px' }}>
                            <Group justify="space-between" align="flex-start">
                              <div>
                                <Text size="sm" fw={700} c={q.color}>{q.label}</Text>
                                <Text size="xs" c="dimmed" mb={8}>{q.desc}</Text>
                              </div>
                              <Badge variant="light" color="violet" size="sm">
                                {q.excl.toLocaleString()} exclusion rules ({exclPct}%)
                              </Badge>
                            </Group>
                            <Text size="xl" fw={700}>{q.total.toLocaleString()}</Text>
                            <Text size="xs" c="dimmed" mb={8}>{pct}% of total</Text>
                            <div style={{ display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden', marginBottom: '6px' }}>
                              <div style={{ width: `${exclPct}%`, background: '#8B5CF6' }} />
                              <div style={{ width: `${otherPct}%`, background: q.color }} />
                            </div>
                            <Group gap={12}>
                              <Text size="xs"><span style={{ color: '#8B5CF6', fontWeight: 700 }}>{q.excl.toLocaleString()}</span> Exclusion Rules</Text>
                              <Text size="xs"><span style={{ color: q.color, fontWeight: 700 }}>{other.toLocaleString()}</span> Other</Text>
                            </Group>
                          </div>
                        )
                      })}
                    </Stack>
                  )
                })()}
              </Stack>
            </Card>

            {/* Exclusion Rules Venn Diagram */}
            {stats?.heatmap && (() => {
              const noteRule = heatmapData.reduce((s, d) => s + d.exclusionCount, 0)
              const aiExcl = heatmapData.reduce((s, d) => s + d.aiExclusionCount, 0)
              const both = heatmapData.reduce((s, d) => s + d.bothExclusionCount, 0)
              const noteOnly = noteRule - both
              const aiOnly = aiExcl - both
              const union = noteOnly + both + aiOnly
              const totalOffenses = heatmapData.reduce((s, d) => s + d.total, 0)

              const notePct = totalOffenses > 0 ? ((noteRule / totalOffenses) * 100).toFixed(1) : '0.0'
              const aiPct = totalOffenses > 0 ? ((aiExcl / totalOffenses) * 100).toFixed(1) : '0.0'
              const unionPct = totalOffenses > 0 ? ((union / totalOffenses) * 100).toFixed(1) : '0.0'

              const maxCircle = Math.max(noteRule, aiExcl, 1)
              const noteRadius = Math.max(60, Math.sqrt(noteRule / maxCircle) * 120)
              const aiRadius = Math.max(60, Math.sqrt(aiExcl / maxCircle) * 120)
              const svgW = 420
              const svgH = 280
              const cx1 = svgW / 2 - 50
              const cx2 = svgW / 2 + 50
              const cy = svgH / 2

              return (
                <>
                  <Title order={3}>Exclusion Rules Overlap</Title>
                  <Card shadow="sm" padding="lg" radius="md" withBorder>
                    <Stack gap="md">
                      <Text size="xs" c="dimmed">
                        Comparing two signals: offenses where the <b>reviewer note</b> contains "Rule #" vs offenses where the <b>AI rules_applied</b> contains "Exclusion".
                      </Text>

                      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        {/* SVG Venn diagram */}
                        <svg width={svgW} height={svgH} style={{ flexShrink: 0 }}>
                          <circle cx={cx1} cy={cy} r={noteRadius} fill="#8B5CF6" fillOpacity={0.25} stroke="#8B5CF6" strokeWidth={2} />
                          <circle cx={cx2} cy={cy} r={aiRadius} fill="#2ECC71" fillOpacity={0.25} stroke="#2ECC71" strokeWidth={2} />

                          <text x={cx1 - 40} y={cy - 10} textAnchor="middle" fontSize="20" fontWeight="700" fill="#5B21B6">{noteOnly.toLocaleString()}</text>
                          <text x={cx1 - 40} y={cy + 12} textAnchor="middle" fontSize="11" fill="#7C3AED">note only</text>

                          <text x={(cx1 + cx2) / 2} y={cy - 10} textAnchor="middle" fontSize="20" fontWeight="700" fill="#1F2937">{both.toLocaleString()}</text>
                          <text x={(cx1 + cx2) / 2} y={cy + 12} textAnchor="middle" fontSize="11" fill="#6B7280">both</text>

                          <text x={cx2 + 40} y={cy - 10} textAnchor="middle" fontSize="20" fontWeight="700" fill="#047857">{aiOnly.toLocaleString()}</text>
                          <text x={cx2 + 40} y={cy + 12} textAnchor="middle" fontSize="11" fill="#059669">AI only</text>

                          <text x={cx1} y={svgH - 10} textAnchor="middle" fontSize="12" fontWeight="600" fill="#8B5CF6">Note contains "Rule #"</text>
                          <text x={cx2} y={svgH - 10} textAnchor="middle" fontSize="12" fontWeight="600" fill="#2ECC71">AI rules_applied "Exclusion"</text>
                        </svg>

                        {/* Summary stats */}
                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <Stack gap={12}>
                            <div style={{ padding: '12px', background: '#FAF5FF', border: '1px solid #DDD6FE', borderRadius: '8px' }}>
                              <Text size="xs" c="dimmed">Set A: Reviewer Note "Rule #"</Text>
                              <Text size="lg" fw={700} c="#5B21B6">{noteRule.toLocaleString()}</Text>
                              <Text size="xs" c="dimmed">{notePct}% of all offenses</Text>
                            </div>
                            <div style={{ padding: '12px', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '8px' }}>
                              <Text size="xs" c="dimmed">Set B: AI Applied "Exclusion" Rule</Text>
                              <Text size="lg" fw={700} c="#047857">{aiExcl.toLocaleString()}</Text>
                              <Text size="xs" c="dimmed">{aiPct}% of all offenses</Text>
                            </div>
                            <div style={{ padding: '12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                              <Group justify="space-between">
                                <div>
                                  <Text size="xs" c="dimmed">Overlap (both)</Text>
                                  <Text size="lg" fw={700}>{both.toLocaleString()}</Text>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <Text size="xs" c="dimmed">Union (either)</Text>
                                  <Text size="lg" fw={700}>{union.toLocaleString()}</Text>
                                  <Text size="xs" c="dimmed">{unionPct}% of all offenses</Text>
                                </div>
                              </Group>
                            </div>
                            {noteOnly > 0 && (
                              <Text size="xs" c="dimmed">
                                {noteOnly.toLocaleString()} offenses have a reviewer "Rule #" note but the AI did not apply an Exclusion rule.
                              </Text>
                            )}
                            {aiOnly > 0 && (
                              <Text size="xs" c="dimmed">
                                {aiOnly.toLocaleString()} offenses had an AI Exclusion rule applied but no reviewer "Rule #" note.
                              </Text>
                            )}
                          </Stack>
                        </div>
                      </div>
                    </Stack>
                  </Card>
                </>
              )
            })()}


            {/* Routing Accuracy by Queue */}
            <Title order={3}>Routing Accuracy by Queue</Title>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Text fw={600} size="lg">Routing Accuracy by Queue</Text>
                <Text size="xs" c="dimmed">
                  Correct = queue matched the UBS outcome: Automation when UBS found Not Reportable, Auditor when UBS found Reportable.
                  Human Intervention and offenses without a UBS label are excluded.
                </Text>
                {stats && (() => {
                  const queueAccuracy = (summaryStats?.queueAccuracy || []).map(q => ({
                    queue: q.queue.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                    accuracy: q.accuracy,
                    count: q.total,
                    accurate: q.accurate,
                  })).filter(q => q.count > 0)

                  return (
                    <>
                      <BarChart
                        h={300}
                        data={queueAccuracy}
                        dataKey="queue"
                        series={[{ name: 'accuracy', color: '#3B82F6', label: 'Accuracy %' }]}
                        tickLine="y"
                        withLegend={false}
                        yAxisProps={{ domain: [0, 100] }}
                      />
                      <Grid>
                        {queueAccuracy.map(q => (
                          <Grid.Col key={q.queue} span={4}>
                            <Paper p="md" withBorder>
                              <Stack gap="xs">
                                <Text size="sm" fw={600}>{q.queue}</Text>
                                <Text size="xl" fw={700} c="blue">
                                  {q.accuracy.toFixed(1)}%
                                </Text>
                                <Text size="xs" c="dimmed">
                                  {q.accurate.toLocaleString()} correct out of {q.count.toLocaleString()}
                                </Text>
                              </Stack>
                            </Paper>
                          </Grid.Col>
                        ))}
                      </Grid>
                    </>
                  )
                })()}
              </Stack>
            </Card>
          </>
        )}

        {/* Offense-Level Routing Explorer - always visible */}
        <Title order={3}>Offense-Level Routing Explorer</Title>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            {/* Filters Row 1 */}
            <Grid>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <DatePickerInput
                      type="range"
                      allowSingleDateInRange
                      label="Created Date Range"
                      placeholder="Select date range"
                      value={dateRange}
                      onChange={setDateRange}
                      leftSection={<IconCalendar size={16} />}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <TextInput
                      label="Search ID"
                      placeholder="Enter search_id..."
                      leftSection={<IconSearch size={16} />}
                      value={searchId}
                      onChange={(e) => setSearchId(e.target.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Product"
                      placeholder="All products"
                      data={stats?.availableSearchTypes?.map((t: string) => ({ value: t, label: t })) || []}
                      value={tableProductFilter}
                      onChange={setTableProductFilter}
                      clearable
                      searchable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Queue"
                      placeholder="All queues"
                      data={[
                        { value: 'AUTOMATION', label: 'Automation' },
                        { value: 'AUDITOR', label: 'Auditor' },
                        { value: 'INSUFFICIENT_DATA', label: 'Human Intervention' },
                      ]}
                      value={queueFilter}
                      onChange={setQueueFilter}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="AI Identity Match Level"
                      placeholder="All levels"
                      data={[
                        { value: 'High', label: 'High' },
                        { value: 'Medium', label: 'Medium' },
                        { value: 'Not Enough Info', label: 'Not Enough Info' },
                        { value: 'Not Matching', label: 'Not Matching' },
                        { value: 'Unknown', label: 'Unknown' },
                      ]}
                      value={identityLevelFilter}
                      onChange={setIdentityLevelFilter}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Reportability"
                      placeholder="All"
                      data={[
                        { value: 'Reportable', label: 'Reportable' },
                        { value: 'Not Reportable', label: 'Not Reportable' },
                        { value: 'Requires Review', label: 'Requires Review' },
                        { value: 'Unknown', label: 'Unknown' },
                      ]}
                      value={reportabilityFilter}
                      onChange={setReportabilityFilter}
                      clearable
                    />
                  </Grid.Col>
                </Grid>

                {/* Filters Row 2 */}
                <Grid>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Match Status"
                      placeholder="All"
                      data={[
                        { value: 'MATCH', label: 'Match' },
                        { value: 'MISMATCH', label: 'Mismatch' },
                        { value: 'UNKNOWN', label: 'Unknown' },
                      ]}
                      value={matchStatusFilter}
                      onChange={setMatchStatusFilter}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="ID Mismatch (Notes)"
                      placeholder="All"
                      data={[
                        { value: 'YES', label: 'Has ID Mismatch Keywords' },
                        { value: 'NO', label: 'No ID Mismatch Keywords' },
                      ]}
                      value={idMatchValidatedFilter}
                      onChange={setIdMatchValidatedFilter}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Duplicate (Notes)"
                      placeholder="All"
                      data={[
                        { value: 'YES', label: 'Has Duplicate Keywords' },
                        { value: 'NO', label: 'No Duplicate Keywords' },
                      ]}
                      value={duplicateFilter}
                      onChange={setDuplicateFilter}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="UBS Status"
                      placeholder="All offenses"
                      data={[
                        { value: 'IGNORED', label: 'Ignored by UBS' },
                        { value: 'REMAINING', label: 'Remaining (Not Ignored)' },
                      ]}
                      value={ignoredByUbsFilter}
                      onChange={setIgnoredByUbsFilter}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="UBS Exclusion"
                      placeholder="All offenses"
                      data={[
                        { value: 'YES', label: 'Has Note Exclusion Rule' },
                        { value: 'NO', label: 'No Note Exclusion Rule' },
                      ]}
                      value={ubsExclusionFilter}
                      onChange={setUbsExclusionFilter}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="AI Exclusion Rule"
                      placeholder="All offenses"
                      data={[
                        { value: 'YES', label: 'Has AI Exclusion Rule' },
                        { value: 'NO', label: 'No AI Exclusion Rule' },
                      ]}
                      value={aiExclusionFilter}
                      onChange={setAiExclusionFilter}
                      clearable
                    />
                  </Grid.Col>
                </Grid>

                {/* Filters Row 3 */}
                <Grid>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Type Class"
                      placeholder="All types"
                      data={[
                        { value: 'MISDEMEANOR', label: 'Misdemeanor' },
                        { value: 'FELONY', label: 'Felony' },
                        { value: 'INFRACTION', label: 'Infraction' },
                        { value: 'OTHER', label: 'Other' },
                      ]}
                      value={typeClassFilter}
                      onChange={setTypeClassFilter}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Offense Age"
                      placeholder="All ages"
                      data={[
                        { value: 'UNDER_7', label: 'Under 7 years' },
                        { value: '7_TO_10', label: '7–10 years' },
                        { value: 'OVER_10', label: 'Over 10 years' },
                      ]}
                      value={offenseAgeFilter}
                      onChange={setOffenseAgeFilter}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Disp Date"
                      placeholder="All offenses"
                      data={[
                        { value: 'YES', label: 'Has Disp Date' },
                        { value: 'NO', label: 'No Disp Date' },
                      ]}
                      value={hasDispDateFilter}
                      onChange={setHasDispDateFilter}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <MultiSelect
                      label="Validation Issues"
                      placeholder="All offenses"
                      data={[
                        { group: 'Status', items: [
                          { value: 'HAS_ERRORS', label: 'Has Validation Errors' },
                          { value: 'HAS_WARNINGS', label: 'Has Warnings' },
                          { value: 'NO_ISSUES', label: 'No Validation Issues' },
                        ]},
                        { group: 'Missing Data', items: [
                          { value: 'MISSING_DISPOSITION', label: 'Disposition missing' },
                          { value: 'MISSING_CHARGE_TYPE', label: 'Charge type missing' },
                          { value: 'MISSING_CHARGE_DESC', label: 'Charge description missing' },
                          { value: 'MISSING_AGE', label: 'Age info missing' },
                          { value: 'MISSING_DISP_DATE', label: 'Disposition date missing' },
                          { value: 'NO_TIMING_DATE', label: 'No usable timing date' },
                        ]},
                        { group: 'Quality Issues', items: [
                          { value: 'FALLBACK_DATE', label: 'Fallback date used' },
                          { value: 'JAIL_INCARCERATION', label: 'Jail/incarceration mentioned' },
                          { value: 'LOW_CONFIDENCE', label: 'Low ensemble confidence' },
                        ]},
                        { group: 'Classification Mismatch', items: [
                          { value: 'DISPOSITION_MISMATCH', label: 'Disposition class mismatch' },
                          { value: 'TYPE_MISMATCH', label: 'Type class mismatch' },
                          { value: 'CRIME_CATEGORY_MISMATCH', label: 'Crime category mismatch' },
                          { value: 'CHARGE_NORM_MISSING', label: 'Charge norm indeterminate' },
                        ]},
                      ]}
                      value={validationStatusFilter}
                      onChange={setValidationStatusFilter}
                      clearable
                      searchable
                      maxDropdownHeight={350}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <Select
                      label="Issue Count"
                      placeholder="Any count"
                      data={[
                        { value: 'ONE', label: 'Exactly 1 issue' },
                        { value: 'MORE_THAN_ONE', label: 'More than 1 issue' },
                      ]}
                      value={validationCountFilter}
                      onChange={setValidationCountFilter}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <Select
                      label="Eval Run"
                      placeholder="All"
                      data={[
                        { group: 'Filter', items: [
                          { value: 'MULTI', label: 'Multiple runs (>1)' },
                          { value: 'CHANGED', label: 'Decision changed' },
                        ]},
                        { group: 'Specific run', items: [
                          { value: 'V1', label: 'v1 (first run)' },
                          { value: 'V2', label: 'v2' },
                          { value: 'V3', label: 'v3' },
                          { value: 'V4', label: 'v4' },
                          { value: 'V5', label: 'v5+' },
                        ]},
                      ]}
                      value={versionFilter}
                      onChange={setVersionFilter}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="QA Override"
                      placeholder="All offenses"
                      data={[
                        { value: 'HAS_OVERRIDE', label: 'Has QA Override' },
                        { value: 'NO_OVERRIDE', label: 'No QA Override' },
                      ]}
                      value={qaOverrideFilter}
                      onChange={setQaOverrideFilter}
                      clearable
                    />
                  </Grid.Col>
                </Grid>

                {/* Column Selection */}
                <Stack gap="xs">
                  <Text size="sm" fw={500}>Select Columns to Display</Text>
                  <Text size="xs" c="dimmed">{selectedColumns.length} of {availableColumns.length} columns selected</Text>
                  <Group gap="xs">
                    <Chip.Group multiple value={selectedColumns} onChange={setSelectedColumns}>
                      {availableColumns.map(col => (
                        <Chip key={col.value} value={col.value} size="xs">
                          {col.label}
                        </Chip>
                      ))}
                    </Chip.Group>
                  </Group>
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={() => setSelectedColumns(isExpert ? expertDefaultColumns : defaultColumns)}
                    >
                      Reset to Default
                    </Button>
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={() => setSelectedColumns(availableColumns.map(c => c.value))}
                    >
                      Select All
                    </Button>
                  </Group>
                </Stack>

                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Showing {rows.length.toLocaleString()} of {totalCount.toLocaleString()} offenses (Page {page} of {totalPages})
                  </Text>
                  <Group gap="xs">
                    <Button
                      leftSection={isExporting ? <IconLoader size={16} className="animate-spin" /> : <IconDownload size={16} />}
                      onClick={downloadCSV}
                      disabled={totalCount === 0 || isExporting}
                      loading={isExporting}
                      variant="filled"
                      size="sm"
                    >
                      {isExporting
                        ? `Exporting${exportProgress ? ` (${exportProgress})` : ''}...`
                        : `Download All (${totalCount.toLocaleString()} records)`}
                    </Button>
                    {(searchId || queueFilter.length > 0 || identityLevelFilter.length > 0 || reportabilityFilter.length > 0 || matchStatusFilter.length > 0 || idMatchValidatedFilter.length > 0 || duplicateFilter.length > 0 || ignoredByUbsFilter.length > 0 || ubsExclusionFilter.length > 0 || aiExclusionFilter.length > 0 || typeClassFilter.length > 0 || offenseAgeFilter.length > 0 || hasDispDateFilter.length > 0 || qaOverrideFilter.length > 0) && (
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => {
                          setSearchId('')
                          setQueueFilter([])
                          setIdentityLevelFilter([])
                          setReportabilityFilter([])
                          setMatchStatusFilter([])
                          setIdMatchValidatedFilter([])
                          setDuplicateFilter([])
                          setIgnoredByUbsFilter([])
                          setUbsExclusionFilter([])
                          setAiExclusionFilter([])
                          setTypeClassFilter([])
                          setOffenseAgeFilter([])
                          setHasDispDateFilter([])
                          setQaOverrideFilter([])
                          setPage(1)
                        }}
                      >
                        Clear Filters
                      </Button>
                    )}
                    {totalPages > 1 && (
                      <Pagination
                        total={totalPages}
                        value={page}
                        onChange={setPage}
                        size="sm"
                      />
                    )}
                  </Group>
                </Group>

                <ScrollArea style={{ position: 'relative', opacity: isRowsFetching ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                  {isRowsFetching && (
                    <Center style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
                      <Loader size="md" />
                    </Center>
                  )}
                  <Table striped highlightOnHover withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        {selectedColumns.map(col => {
                          const colDef = allAvailableColumns.find(c => c.value === col)
                          return <Table.Th key={col}>{colDef?.label || col}</Table.Th>
                        })}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {rows.map((record, index) => {
                        // Helper to render cell value with appropriate formatting
                        const renderCell = (colValue: string) => {
                          const value = (record as any)[colValue]

                          // Helper for rendering arrays/objects
                          const renderValue = (val: any): string => {
                            if (val === null || val === undefined) return 'None'
                            if (Array.isArray(val)) return val.join(', ')
                            if (typeof val === 'object') return JSON.stringify(val)
                            return String(val)
                          }

                          switch (colValue) {
                            case 'search_id':
                            case 'offense_id':
                              return <Text size="xs" ff="monospace">{value || 'None'}</Text>

                            case 'identity_level':
                              return (
                                <Badge
                                  size="sm"
                                  color={
                                    value === 'High' ? 'green' :
                                    value === 'Medium' ? 'orange' :
                                    value === 'Not Matching' ? 'red' :
                                    value === 'Not Enough Info' ? 'yellow' : 'gray'
                                  }
                                >
                                  {value || 'None'}
                                </Badge>
                              )

                            case 'reportability':
                              return (
                                <Badge
                                  size="sm"
                                  color={
                                    value === 'Reportable' ? 'red' :
                                    value === 'Not Reportable' ? 'green' :
                                    value === 'Requires Review' ? 'yellow' : 'gray'
                                  }
                                  variant="light"
                                >
                                  {value || 'None'}
                                </Badge>
                              )

                            case 'queue':
                              return (
                                <Badge
                                  size="sm"
                                  color={
                                    (value?.toUpperCase() === 'AUTOMATION') ? 'green' :
                                    (value?.toUpperCase() === 'AUDITOR') ? 'orange' : 'gray'
                                  }
                                >
                                  {value || 'None'}
                                </Badge>
                              )

                            case 'match_status':
                              return (
                                <Badge
                                  size="sm"
                                  color={value === 'MATCH' ? 'green' : value === 'MISMATCH' ? 'red' : 'gray'}
                                >
                                  {value || 'None'}
                                </Badge>
                              )

                            case 'error_type':
                              return (
                                <Badge
                                  size="sm"
                                  color={
                                    value === 'CORRECT' ? 'green' :
                                    value === 'FALSE_POSITIVE' ? 'orange' :
                                    value === 'FALSE_NEGATIVE' ? 'red' : 'gray'
                                  }
                                  variant="light"
                                >
                                  {value || 'None'}
                                </Badge>
                              )

                            case 'id_match_validated':
                              return (
                                <Badge
                                  size="sm"
                                  color={value === 'ID_MATCH' ? 'green' : 'red'}
                                  variant="light"
                                >
                                  {value || 'None'}
                                </Badge>
                              )

                            case 'duplicate':
                              return (
                                <Badge
                                  size="sm"
                                  color={value === 'YES' ? 'red' : 'green'}
                                  variant="light"
                                >
                                  {value || 'None'}
                                </Badge>
                              )

                            case 'routing_accuracy': {
                              const isAccurate = value === true || value === 'true'
                              const isInaccurate = value === false || value === 'false'
                              return (
                                <Badge
                                  size="sm"
                                  color={isAccurate ? 'green' : isInaccurate ? 'red' : 'gray'}
                                >
                                  {isAccurate ? 'Accurate' : isInaccurate ? 'Inaccurate' : 'N/A'}
                                </Badge>
                              )
                            }

                            case 'ce_decision': {
                              const displayDecision = (record as any).version_decision || value
                              return (
                                <Badge
                                  size="sm"
                                  color={displayDecision === 'REPORTABLE' ? 'red' : displayDecision === 'NOT_REPORTABLE' ? 'green' : displayDecision === 'REQUIRES_REVIEW' ? 'orange' : 'gray'}
                                >
                                  {displayDecision || 'None'}
                                </Badge>
                              )
                            }

                            case 'qa_decision': {
                              return (
                                <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                                  <Select
                                    size="xs"
                                    placeholder="—"
                                    data={[
                                      { value: 'REPORTABLE', label: 'Reportable' },
                                      { value: 'NOT_REPORTABLE', label: 'Not Reportable' },
                                    ]}
                                    value={value || null}
                                    onChange={(newValue) => {
                                      if (newValue && newValue !== value) {
                                        qaDecisionMutation.mutate(
                                          {
                                            offenseId: record.offense_id,
                                            searchId: record.search_id,
                                            qaDecision: newValue,
                                          },
                                          {
                                            onSuccess: () => {
                                              notifications.show({
                                                title: 'QA Decision Updated',
                                                message: `Set to ${newValue === 'REPORTABLE' ? 'Reportable' : 'Not Reportable'}`,
                                                color: 'green',
                                              })
                                            },
                                            onError: () => {
                                              notifications.show({
                                                title: 'Update Failed',
                                                message: 'Could not save QA decision. Please try again.',
                                                color: 'red',
                                              })
                                            },
                                          }
                                        )
                                      }
                                    }}
                                    allowDeselect={false}
                                    styles={{ input: { minWidth: 130 } }}
                                  />
                                </div>
                              )
                            }

                            // Text fields that may be long - show truncated with tooltip
                            case 'charge':
                            case 'charge_description':
                            case 'disposition':
                            case 'rationale':
                            case 'rules_applied':
                            case 'all_notes':
                            case 'additional_info':
                            case 'sentence_info':
                              return <Text size="xs" lineClamp={2}>{renderValue(value)}</Text>

                            case 'sentence_is_serving':
                              if (value === null || value === undefined) return <Text size="xs">None</Text>
                              return <Text size="xs">{value ? 'Yes' : 'No'}</Text>

                            // Date fields
                            case 'label_created_at':
                            case 'charge_date':
                            case 'arrest_date':
                            case 'disposition_date':
                            case 'effective_release_date':
                              if (!value) return <Text size="xs">None</Text>
                              try {
                                // BigQuery dates may come as objects with a `value` property
                                const dateValue = typeof value === 'object' && value?.value ? value.value : value
                                if (!dateValue) return <Text size="xs">None</Text>
                                const date = new Date(dateValue)
                                if (isNaN(date.getTime())) return <Text size="xs">None</Text>
                                return <Text size="xs">{date.toLocaleDateString()}</Text>
                              } catch {
                                return <Text size="xs">{String(value)}</Text>
                              }

                            // Classification badges
                            case 'type_class':
                            case 'subtype_class':
                            case 'disp_class':
                            case 'adjudication_category':
                            case 'crime_category':
                            case 'court_type':
                            case 'age_category':
                              return (
                                <Badge size="sm" variant="light" color="blue">
                                  {value || 'None'}
                                </Badge>
                              )

                            case 'current_version_number': {
                              const vNum = Number(value) || 1
                              return (
                                <Badge size="sm" variant="light" color={vNum > 1 ? 'blue' : 'gray'}>
                                  Run {vNum}
                                </Badge>
                              )
                            }

                            case 'prev_decision': {
                              if (!value) {
                                return <Text size="xs" c="dimmed">—</Text>
                              }
                              const currentDec = (record as any).version_decision || (record as any).ce_decision
                              const changed = currentDec && value !== currentDec
                              return (
                                <Badge
                                  size="sm"
                                  color={value === 'REPORTABLE' ? 'red' : value === 'NOT_REPORTABLE' ? 'green' : 'gray'}
                                  variant={changed ? 'filled' : 'light'}
                                >
                                  {value}
                                  {changed ? ' →' : ''}
                                </Badge>
                              )
                            }

                            case 'version_count': {
                              const count = Number(value) || 0
                              const hasChange = (record as any).has_decision_change
                              const searchIdForPopover = (record as any).search_id
                              const isPopoverOpen = versionPopoverSearchId === searchIdForPopover

                              if (count <= 1) {
                                return <Badge size="sm" variant="light" color="gray">1</Badge>
                              }

                              return (
                                <Popover
                                  opened={isPopoverOpen}
                                  onClose={() => setVersionPopoverSearchId(null)}
                                  position="bottom-start"
                                  withArrow
                                  shadow="md"
                                  width={420}
                                >
                                  <Popover.Target>
                                    <Badge
                                      size="sm"
                                      color={hasChange ? 'orange' : 'blue'}
                                      variant={hasChange ? 'filled' : 'light'}
                                      style={{ cursor: 'pointer' }}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setVersionPopoverSearchId(isPopoverOpen ? null : searchIdForPopover)
                                      }}
                                    >
                                      {hasChange ? `${count} Changed` : `${count} runs`}
                                    </Badge>
                                  </Popover.Target>
                                  <Popover.Dropdown onClick={(e) => e.stopPropagation()}>
                                    <Stack gap="xs">
                                      <Text size="sm" fw={600}>Evaluation Runs</Text>
                                      {evaluationsForPopover.isLoading && (
                                        <Center py="xs"><Loader size="xs" /></Center>
                                      )}
                                      {evaluationsForPopover.isSuccess && (
                                        <Table striped highlightOnHover withTableBorder>
                                          <Table.Thead>
                                            <Table.Tr>
                                              <Table.Th style={{ fontSize: 11 }}>Run</Table.Th>
                                              <Table.Th style={{ fontSize: 11 }}>Decision</Table.Th>
                                              <Table.Th style={{ fontSize: 11 }}>Queue</Table.Th>
                                              <Table.Th style={{ fontSize: 11 }}>Source</Table.Th>
                                              <Table.Th style={{ fontSize: 11 }}>Date</Table.Th>
                                            </Table.Tr>
                                          </Table.Thead>
                                          <Table.Tbody>
                                            {((evaluationsForPopover.data as EvaluationVersion[]) || []).map((ev, idx, arr) => {
                                              const vNum = arr.length - idx
                                              return (
                                                <Table.Tr
                                                  key={ev.correlation_id || idx}
                                                  style={{ cursor: 'pointer' }}
                                                  onClick={() => {
                                                    setVersionPopoverSearchId(null)
                                                    setSelectedOffense(record)
                                                    setOpenHistoryTab(true)
                                                    setDialogOpened(true)
                                                  }}
                                                >
                                                  <Table.Td>
                                                    <Group gap={4}>
                                                      <Text size="xs" fw={600}>Run {vNum}</Text>
                                                      {idx === 0 && <Badge size="xs" color="blue">latest</Badge>}
                                                    </Group>
                                                  </Table.Td>
                                                  <Table.Td>
                                                    <Badge
                                                      size="xs"
                                                      color={ev.record_decision === 'REPORTABLE' ? 'red' : ev.record_decision === 'NOT_REPORTABLE' ? 'green' : 'gray'}
                                                    >
                                                      {ev.record_decision || 'N/A'}
                                                    </Badge>
                                                  </Table.Td>
                                                  <Table.Td>
                                                    <Text size="xs">{ev.search_queue || 'N/A'}</Text>
                                                  </Table.Td>
                                                  <Table.Td>
                                                    <Text size="xs">{ev.source || '—'}</Text>
                                                  </Table.Td>
                                                  <Table.Td>
                                                    <Text size="xs">
                                                      {ev.publish_time ? new Date(ev.publish_time).toLocaleDateString() : '—'}
                                                    </Text>
                                                  </Table.Td>
                                                </Table.Tr>
                                              )
                                            })}
                                          </Table.Tbody>
                                        </Table>
                                      )}
                                      <Text size="xs" c="dimmed">Click any run to view full history</Text>
                                    </Stack>
                                  </Popover.Dropdown>
                                </Popover>
                              )
                            }

                            // Actions column (admin only)
                            case 'actions':
                              return isAdmin ? (
                                <SendToExpertButton offense={record as any} size="xs" />
                              ) : null

                            default:
                              return <Text size="xs">{renderValue(value)}</Text>
                          }
                        }

                        return (
                          <Table.Tr
                            key={`${record.offense_id}-v${(record as any).current_version_number || 1}-${index}`}
                            onClick={() => {
                              setSelectedOffense(record)
                              setDialogOpened(true)
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {selectedColumns.map(col => (
                              <Table.Td key={col}>{renderCell(col)}</Table.Td>
                            ))}
                          </Table.Tr>
                        )
                      })}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>

                {/* Bottom Pagination */}
                {totalPages > 1 && (
                  <Group justify="center">
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                    />
                  </Group>
                )}
          </Stack>
        </Card>

        {totalCount === 0 && !isLoading && (
          <Center h={200}>
            <Text c="dimmed">No routing data available for the selected filters</Text>
          </Center>
        )}
      </Stack>

      {/* Offense Detail Dialog */}
      <OffenseDetailDialog
        opened={dialogOpened}
        onClose={() => {
          setDialogOpened(false)
          setSelectedOffense(null)
          setOpenHistoryTab(false)
        }}
        offense={selectedOffense as any}
        allOffensesInSearch={offensesBySearchData?.rows || []}
        isExpert={isExpert}
        initialTab={openHistoryTab ? 'history' : undefined}
      />
    </Container>
  )
}
